import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Button,
  TextField,
  Paper,
  CircularProgress,
  Grid,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
  Tooltip,
  Collapse,
  List,
  ListItem,
  ListItemText,
  Checkbox
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  KeyboardArrowDown as ExpandMoreIcon,
  KeyboardArrowUp as ExpandLessIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { adminService } from '../../../services/adminService';

const TeamGroupManagementPanel = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [teamGroups, setTeamGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expandedGroup, setExpandedGroup] = useState(null);
  const [selectedTeams, setSelectedTeams] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [teamSearchOpen, setTeamSearchOpen] = useState(false);
  const [groupFormOpen, setGroupFormOpen] = useState(false);
  const [groupFormData, setGroupFormData] = useState({
    name: '',
    description: ''
  });
  const [error, setError] = useState(null);
  const [editGroupFormOpen, setEditGroupFormOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [groupToDelete, setGroupToDelete] = useState(null);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [filteredTeams, setFilteredTeams] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [teamsLoading, setTeamsLoading] = useState(false);
  
  // Use constants instead of state for these values since they don't change
  // Using standardized parameters for consistent team loading across components

  // Add loading ref to avoid infinite loops
  const loadingRef = useRef(false);

  // Load team groups
  const loadData = useCallback(async () => {
    if (loadingRef.current) return;
    
    try {
      loadingRef.current = true;
      setLoading(true);
      setError(null);
      
      // Load team groups
      const groupsResponse = await adminService.listTeamGroups();
      setTeamGroups(Array.isArray(groupsResponse) ? groupsResponse : []);
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError('Failed to load data. Please try again.');
      enqueueSnackbar(error.message || 'An error occurred', { variant: 'error' });
    } finally {
      setLoading(false);
      loadingRef.current = false;
    }
  }, [enqueueSnackbar]);

  // Dedicated function to load teams matching TeamManagementPanel approach
  const loadTeams = useCallback(async () => {
    try {
      setTeamsLoading(true);
      
      // Use standardized parameters to ensure consistent caching
      const response = await adminService.listTeams(0, 1000, enqueueSnackbar, true);
      
      // Handle standardized response format - always expect response.teams
      let teamsData = [];
      
      if (response && response.teams && Array.isArray(response.teams)) {
        teamsData = response.teams;
      } else {
        console.warn('Unexpected response format from listTeams:', response);
      }
      
      setAvailableTeams(teamsData);
      setFilteredTeams(teamsData);
    } catch (error) {
      console.error('Error loading teams:', error);
      enqueueSnackbar('Failed to load teams', { variant: 'error' });
    } finally {
      setTeamsLoading(false);
    }
  }, [enqueueSnackbar]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleCreateGroup = async () => {
    try {
      setLoading(true);
      setError(null);
      await adminService.createTeamGroup(groupFormData);
      await loadData();
      setGroupFormOpen(false);
      setGroupFormData({ name: '', description: '' });
      enqueueSnackbar('Team group created successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error creating team group:', error);
      setError('Failed to create team group. Please try again.');
      enqueueSnackbar(error.message || 'An error occurred', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveTeam = async (groupId, teamId) => {
    if (window.confirm('Are you sure you want to remove this team from the group?')) {
      try {
        setLoading(true);
        setError(null);
        await adminService.removeTeamFromGroup(groupId, teamId);
        await loadData();
        enqueueSnackbar('Team removed successfully', { variant: 'success' });
      } catch (error) {
        console.error('Error removing team:', error);
        setError('Failed to remove team from group');
        enqueueSnackbar(error.message || 'An error occurred', { variant: 'error' });
      } finally {
        setLoading(false);
      }
    }
  };

  const handleTeamSelection = (team) => {
    setSelectedTeams(prev => {
      const isSelected = prev.find(t => t.id === team.id);
      return isSelected ? 
        prev.filter(t => t.id !== team.id) :
        [...prev, team];
    });
  };

  const handleAddTeams = async () => {
    if (!activeGroup || !selectedTeams.length) return;
    
    try {
      setLoading(true);
      setError(null);
      await adminService.addTeamsToGroup(
        activeGroup.id, 
        selectedTeams.map(team => team.id)
      );
      setTeamSearchOpen(false);
      setSelectedTeams([]);
      setSearchQuery('');
      await loadData();
      enqueueSnackbar('Teams added successfully', { variant: 'success' });
    } catch (error) {
      setError('Failed to add teams. Please try again.');
      enqueueSnackbar(error.message || 'An error occurred', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (group) => {
    setEditingGroup(group);
    setEditGroupFormOpen(true);
  };

  const handleDeleteClick = (group) => {
    setGroupToDelete(group);
    setDeleteConfirmOpen(true);
  };

  const handleEditGroup = async () => {
    try {
      setLoading(true);
      setError(null);
      await adminService.updateTeamGroup(editingGroup.id, {
        name: editingGroup.name,
        description: editingGroup.description
      });
      await loadData();
      setEditGroupFormOpen(false);
      setEditingGroup(null);
      enqueueSnackbar('Team group updated successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error updating team group:', error);
      setError('Failed to update team group. Please try again.');
      enqueueSnackbar(error.message || 'An error occurred', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteGroup = async () => {
    try {
      setLoading(true);
      setError(null);
      await adminService.deleteTeamGroup(groupToDelete.id);
      await loadData();
      setDeleteConfirmOpen(false);
      setGroupToDelete(null);
      enqueueSnackbar('Team group deleted successfully', { variant: 'success' });
    } catch (error) {
      console.error('Error deleting team group:', error);
      setError('Failed to delete team group. Please try again.');
      enqueueSnackbar(error.message || 'An error occurred', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleExpand = (groupId) => {
    setExpandedGroup(expandedGroup === groupId ? null : groupId);
  };

  // Handle search query changes
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    
    if (Array.isArray(availableTeams)) {
      const searchTerm = query.toLowerCase().trim();
      const filtered = availableTeams.filter(team => 
        team.name && team.name.toLowerCase().includes(searchTerm)
      );
      setFilteredTeams(filtered);
    }
  };

  // Open team search dialog and load teams
  const handleOpenTeamSearch = async (group) => {
    setActiveGroup(group);
    setSearchQuery('');
    setTeamSearchOpen(true);
    
    // Always reload teams when opening the dialog to ensure we have the latest data
    await loadTeams();
  };

  // Check if a team is already in the active group
  const isTeamInActiveGroup = (teamId) => {
    return activeGroup && 
           activeGroup.teams && 
           Object.prototype.hasOwnProperty.call(activeGroup.teams, teamId);
  };

  return (
    <Box p={3}>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Team Groups Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setGroupFormOpen(true)}
        >
          Create Team Group
        </Button>
      </Box>

      {/* Main Table Container */}
      <TableContainer component={Paper} sx={{ width: '100%' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell width="56px" />
              <TableCell>Group Name</TableCell>
              <TableCell>Description</TableCell>
              <TableCell>Teams</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && teamGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <CircularProgress />
                </TableCell>
              </TableRow>
            ) : teamGroups.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center" sx={{ py: 3 }}>
                  <Typography color="textSecondary">
                    No team groups found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              teamGroups.map((group) => (
                <React.Fragment key={group.id}>
                  <TableRow>
                    <TableCell>
                      <IconButton size="small" onClick={() => handleToggleExpand(group.id)}>
                        {expandedGroup === group.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                      </IconButton>
                    </TableCell>
                    <TableCell>{group.name}</TableCell>
                    <TableCell>{group.description}</TableCell>
                    <TableCell>{group.teams ? Object.keys(group.teams).length : 0} teams</TableCell>
                    <TableCell align="right">
                      <Tooltip title="Add Teams">
                        <IconButton onClick={() => handleOpenTeamSearch(group)}>
                          <AddIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Group">
                        <IconButton onClick={() => handleEditClick(group)}>
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Group">
                        <IconButton color="error" onClick={() => handleDeleteClick(group)}>
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell style={{ padding: 0 }} colSpan={5}>
                      <Collapse in={expandedGroup === group.id} timeout="auto" unmountOnExit>
                        <Box 
                          sx={{ 
                            margin: '2rem 1rem 1rem 4rem',
                            backgroundColor: '#fafafa',
                            borderRadius: '8px',
                            border: '1px solid #e0e0e0',
                            overflow: 'hidden'
                          }}
                        >
                          <Table size="small">
                            <TableHead>
                              <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
                                <TableCell sx={{ fontWeight: 600, width: '30%' }}>Team Name</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Schools</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: '20%' }}>Added Date</TableCell>
                                <TableCell sx={{ fontWeight: 600, width: '10%' }} align="right">Actions</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {group.teams && Object.entries(group.teams)
                                .sort(([, a], [, b]) => (a.name || '').localeCompare(b.name || ''))
                                .map(([teamId, team]) => (
                                  <TableRow 
                                    key={teamId}
                                    sx={{ 
                                      '&:hover': { 
                                        backgroundColor: '#f5f5f5'
                                      }
                                    }}
                                  >
                                    <TableCell sx={{ color: 'text.primary' }}>
                                      {team.name || 'N/A'}
                                    </TableCell>
                                    <TableCell>
                                      {team.schoolCount || 0} schools
                                    </TableCell>
                                    <TableCell>
                                      {team.added ? new Date(team.added).toLocaleDateString() : 'N/A'}
                                    </TableCell>
                                    <TableCell align="right">
                                      <IconButton
                                        size="small"
                                        color="error"
                                        sx={{ 
                                          opacity: 0.7,
                                          '&:hover': { 
                                            opacity: 1,
                                            backgroundColor: '#ffebee'
                                          }
                                        }}
                                        onClick={() => handleRemoveTeam(group.id, teamId)}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </TableCell>
                                  </TableRow>
                                ))}
                              {(!group.teams || Object.keys(group.teams).length === 0) && (
                                <TableRow>
                                  <TableCell colSpan={4} align="center" sx={{ py: 3 }}>
                                    <Typography color="textSecondary">
                                      No teams assigned to this group
                                    </Typography>
                                  </TableCell>
                                </TableRow>
                              )}
                            </TableBody>
                          </Table>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Create Group Dialog */}
      <Dialog
        open={groupFormOpen}
        onClose={() => setGroupFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Create New Team Group</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name"
                value={groupFormData.name}
                onChange={(e) => setGroupFormData({ ...groupFormData, name: e.target.value })}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={groupFormData.description}
                onChange={(e) => setGroupFormData({ ...groupFormData, description: e.target.value })}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateGroup}
            disabled={loading || !groupFormData.name}
          >
            {loading ? <CircularProgress size={24} /> : 'Create Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Group Dialog */}
      <Dialog
        open={editGroupFormOpen}
        onClose={() => setEditGroupFormOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Edit Team Group</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Group Name"
                value={editingGroup?.name || ''}
                onChange={(e) => setEditingGroup(prev => ({ ...prev, name: e.target.value }))}
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                value={editingGroup?.description || ''}
                onChange={(e) => setEditingGroup(prev => ({ ...prev, description: e.target.value }))}
                multiline
                rows={3}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditGroupFormOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditGroup}
            disabled={loading || !editingGroup?.name}
          >
            {loading ? <CircularProgress size={24} /> : 'Save Changes'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Delete Team Group</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the team group "{groupToDelete?.name}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteGroup}
            disabled={loading}
          >
            {loading ? <CircularProgress size={24} /> : 'Delete Group'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Team Selection Dialog */}
      <Dialog
        open={teamSearchOpen}
        onClose={() => setTeamSearchOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Add Teams to Group: {activeGroup?.name}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Search Teams"
              variant="outlined"
              value={searchQuery}
              onChange={handleSearchChange}
              sx={{ mb: 2 }}
            />
            <Paper variant="outlined" sx={{ maxHeight: 400, overflow: 'auto' }}>
              {teamsLoading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <List>
                  {Array.isArray(filteredTeams) && filteredTeams.length > 0 ? (
                    <Box sx={{ p: 2, bgcolor: '#f5f5f5' }}>
                      <Typography variant="caption" color="textSecondary">
                        {filteredTeams.length} teams available
                        {searchQuery ? ` matching "${searchQuery}"` : ''}
                      </Typography>
                    </Box>
                  ) : null}
                  
                  {Array.isArray(filteredTeams) && filteredTeams.length > 0 ? (
                    [...filteredTeams]
                      .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      .map((team) => {
                        const isSelected = selectedTeams.some(t => t.id === team.id);
                        const isAlreadyInGroup = isTeamInActiveGroup(team.id);
                        
                        return (
                          <ListItem
                            key={team.id}
                            secondaryAction={
                              <Checkbox
                                edge="end"
                                checked={isSelected}
                                disabled={!!isAlreadyInGroup}
                                onChange={() => handleTeamSelection(team)}
                              />
                            }
                            sx={{
                              opacity: isAlreadyInGroup ? 0.5 : 1,
                              bgcolor: isSelected ? 'action.selected' : 'transparent'
                            }}
                          >
                            <ListItemText
                              primary={team.name || 'Unnamed Team'}
                              secondary={`${team.school_count || 0} schools`}
                            />
                          </ListItem>
                        );
                      })
                  ) : (
                    <ListItem>
                      <ListItemText 
                        primary={searchQuery ? "No teams match your search" : "No teams available"} 
                        sx={{ textAlign: 'center', color: 'text.secondary' }}
                      />
                    </ListItem>
                  )}
                </List>
              )}
            </Paper>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTeamSearchOpen(false)}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddTeams}
            disabled={loading || selectedTeams.length === 0}
            startIcon={loading ? <CircularProgress size={20} /> : null}
          >
            Add Selected Teams ({selectedTeams.length})
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default React.memo(TeamGroupManagementPanel);